<template>
  <ion-modal
    class="modal-big"
    :is-open="isOpen"
    mode="md"
    @didDismiss="dismissModal"
    :backdropDismiss="true"
    :showBackdrop="true"
  >
    <div class="modal-inner-content">
      <ion-header translucent>
        <ion-toolbar>
          <ion-title class="title">{{ modalTitle }}</ion-title>
          <ion-buttons slot="end">
            <ion-button class="close-button" @click="dismissModal()">
              <i class="ti-close" />
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <div class="d-flex justify-content-center align-items-center pb-3 px-3">
        <div class="h-100 w-100">
          <div class="d-flex justify-content-center align-items-center h-75 w-100 mb-3">
            <div v-if="allImages && allImages.length" class="w-100">
              <ClientOnly>
                <swiper
                  :modules="modules"
                  navigation
                  class="visuals mt-3"
                  @slideChange="changeSwiperIndex($event)"
                  :initialSlide="activeIndex"
                  @reachEnd="fetchMoreVisuals"
                >
                  <swiper-slide v-for="(image, index) in allImages" :key="index">
                    <img class="image-contain" v-image :src="isFeatured ? image.visual_url : image.image" />
                  </swiper-slide>
                  ...
                </swiper>
              </ClientOnly>
            </div>
            <img v-else :src="imageUrl" class="visual-prev-img mt-3" />
          </div>

          <div class="h-25 w-100 d-flex align-items-center" v-if="isCreate || editable">
            <div class="w-100">
              <div class="label"><b>Caption:</b></div>
              <ion-input
                class="c-input"
                placeholder="Write a caption for this image"
                :value="caption"
                @input="$emit('update:caption', $event.target.value)"
                maxlength="150"
              />
              <div class="label mt-2"><b>Source:</b></div>
              <ion-input
                class="c-input"
                placeholder="Image credits/source"
                :value="source"
                @input="$emit('update:source', $event.target.value)"
                maxlength="150"
              />
            </div>
          </div>
          <div v-else class="text-center h-25 w-100 d-flex-column align-items-center justify-content-center">
            <i v-if="caption" class="d-block mb-2">{{ caption }} </i>

            <i v-if="sourceUser && sourceUser.username" class="d-block mb-2"
              >Source: Submitted with ❤️ by
              <strong
                ><router-link
                  :to="{
                    name: 'profile',
                    params: { username: sourceUser.username },
                  }"
                  target="_blank"
                  class="text-black"
                  >@{{ sourceUser.username }}</router-link
                ></strong
              ></i
            >
            <i v-else-if="source" class="d-block mb-2">Source: {{ source }}</i>
            <div class="d-flex" :class="isMobSmallScreen ? 'justify-content-start' : ' justify-content-center'">
              <visual-preview-reactions
                :image="image"
                @onReaction="(reactionResp: {}) => onVisualReaction(reactionResp)"
                v-if="
                  ($route.name === 'character-profile' && !isMoodBoard) ||
                  ($route.name === 'character-profile-new' && !isMoodBoard) ||
                  $route.name === 'world-details' ||
                  $route.name === 'social-space-details' ||
                  $route.name === 'reactions' ||
                  $route.name === 'character-profile-new'
                "
              />

              <bookmark-picker
                v-if="imageId && isAuthenticated"
                :objectId="imageId"
                contentType="image"
                class="text-center"
                in-button
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </ion-modal>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import namespace from '@/store/namespace';
import BookmarkPicker from '@/shared-components/BookmarkPicker.vue';
import { Image } from '@/types/static-types';
import VisualPreviewReactions from '@/shared-components/VisualPreviewReactions.vue';
import { authStore } from '~/pinia-store/auth';
import { Navigation, Pagination } from 'swiper';

@Options({
  name: 'VisualPreviewModal',
  components: {
    BookmarkPicker,
    VisualPreviewReactions,
  },
})
export default class VisualPreviewModal extends Vue {
  @Prop({ default: false }) isCreate!: boolean;
  @Prop({ default: false }) editable!: boolean;
  @Prop({}) imageUrl!: string;
  @Prop({ required: false }) caption!: string;
  @Prop({ required: false }) imageId!: string;
  @Prop({ required: false }) source!: string;
  @Prop({ required: false }) sourceUser!: any;
  @Prop({ default: false }) isFeatured!: boolean;
  @Prop({ default: false }) image!: any;
  @Prop({ default: false }) isMoodBoard!: boolean;
  @Prop({ type: Number, default: 0 }) public activeIndex!: number;
  @Prop({ default: () => '', required: false }) modalTitle!: string;
  @Prop({ default: false }) isOpen!: boolean;
  @Prop({}) allImages!: (Image & { visual_url?: string })[];

  public modules = [Navigation, Pagination];

  public get isAuthenticated() {
    const { isAuthenticated } = authStore();
    return isAuthenticated.value;
  }

  @Action('reactImage', { namespace: namespace.ImagesModule })
  public reactImage!: any;

  public get windowWidth() {
    return window.innerWidth;
  }

  public currentIndex = 0;
  public dismissModal() {
    this.$emit('dismissModal');
    this.currentIndex = 0;
  }

  public slideOptions = {
    initialSlide: this.activeIndex,
    speed: 400,
  };
  setup() {
    const slideOpts = {
      initialSlide: this.activeIndex,
      speed: 400,
    };
    return { slideOpts };
  }
  public changeSwiperIndex($event: any) {
    this.currentIndex = $event.activeIndex as number;
    this.$emit('updated', this.currentIndex);
  }
  public fetchMoreVisuals() {
    this.$emit('loadmore');
  }
  public onVisualReaction(reactionResp: any) {
    this.$emit('onReaction', reactionResp);
  }
  public get isMobSmallScreen() {
    return this.windowWidth <= 500;
  }

  @Watch('$route')
  public routeChanged() {
    this.$emit('dismissModal');
  }
}
</script>

<style lang="sass" scoped>
.visuals
  height: 350px
@media (max-width: 480px)
  .visuals
    height: 200px

.modal-inner-content
  background: var(--ion-background-color, #fff)
  max-height: 98vh
  overflow: auto
  overflow-x: hidden

.reaction-count
  font-size: 18px
  font-weight: bold
.image-contain
  width: 100%
  height: 100%
  object-fit: contain
.visual-prev-img
  max-width: 100%
  max-height: 400px
.close-bottom
  position: absolute
  bottom: 1.5rem
  left: 0
  right: 0
  margin-left: auto
  margin-right: auto
  width: 100%
  text-align: center
  div
    display: inline-block
.close-button
  width: 50px
.bp
  position: absolute
  top: -35px
  z-index: 11

.modal-big
  --width: 600px
  --height: auto
  @media(max-width: 576px)
    --width: 100%
</style>
